import React, {useEffect, useState, useContext, useCallback, useMemo} from "react";
import {retrieveContentfulImage} from "../../../common/image";
import {NotificationBanner, ResponsiveDialog, CustomButton} from "@hapinest/theme";
import {useI18N, useLocalizedUrl, useLocation} from "../../../common/common";
import {Box, makeStyles} from "@material-ui/core";
import {useBreakpoints, useKeycloakFunctions, useLocalStorage, useNotificationBanner, useNotificationBannerBySlug, useReservations, useSessionStorage} from "../../../hooks";
import CustomRichText from "../text/CustomRichText";
import moment from "moment";
import {RESERVATION_STATUS, ROLES_MAP, TOP_NOTIFICATION_BANNER_VISIBLE, TRIAL_TRIPS_MAX} from "../../../common/constant";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import BuyAShareDialogWrapper from "../../dialog/membership/BuyAShareDialogWrapper";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";
import {UserContext} from "../../../store/providers/UserProvider";
import NimbusConnectButton from "../button/NimbusConnectButton";
import {checkHideVotingBanner} from "../../../common/configcat/functions";

const useStyles = makeStyles((theme) => ({
    bullets: {
        "& ul": {
            textAlign: "justify",
            [theme.breakpoints.up("sm")]: {
                margin: "auto",
                width: "60%",
                textAlign: "start",
                paddingTop: theme.spacing(2),
                paddingBottom: theme.spacing(2),
            },
        },
    }
}));

const TopNotificationBanner = () => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const {isExtraSmallScreen, isSmallScreen} = useBreakpoints();
    const [closeBannerButtonClicked, setCloseBannerButtonClicked] = useLocalStorage(TOP_NOTIFICATION_BANNER_VISIBLE, {visibility: false, dateFrom: null});
    const {authenticated, isTrialMember, hasOneOfRoles, userId} = useKeycloakFunctions();
    const {location, navigate, isRouteInLocation, isRouteTheExactLocation} = useLocation();
    const {getNotificationBanner, notificationBannerDetails} = useNotificationBanner();
    const {getNotificationBannerBySlug, notificationBannerDetails: notificationBannerBySlugDetails} = useNotificationBannerBySlug();
    const {reservations, getReservations} = useReservations();
    const {sendCustomEvent, eventNames} = useAnalytics();
    const {getLocalizedUrl} = useLocalizedUrl();

    const GA_TRIAL_BANNER_FIRST_IMPRESSION = "GA_TRIAL_BANNER_FIRST_IMPRESSION";
    const [firstImpressionGAEvent, setFirstImpressionGAEvent] = useSessionStorage(GA_TRIAL_BANNER_FIRST_IMPRESSION);

    const {localeUppercase} = useContext(LocaleContext)[0];
    const [userState] = useContext(UserContext);
    const userCountry = userState?.user?.country;
    const userCanVote = userState?.userSettings?.can_vote;

    const [isNotificationBannerVisible, setNotificationBannerVisible] = useState(false);
    const [isOpenDetailsDialog, setDetailsOpenDialog] = useState(false);
    const [isNotificationBannerBySlugClosed, setNotificationBannerBySlugClosed] = useState();
    const [isBuySharesDialogOpen, setIsBuySharesDialogOpen] = useState(false);
    const [trialReachedLimit, setTrialReachedLimit] = useState();
    const [isVotingBannerHidden, setIsVotingBannerHidden] = useState(true);

    const isVisibleForRange = useMemo(() => {
        const visibleFrom = notificationBannerDetails?.visibleFrom;
        const visibleTo = notificationBannerDetails?.visibleTo;

        if (visibleFrom && visibleTo) {
            const isFromInRange = moment().isSameOrAfter(visibleFrom, "day");
            const isToInRange = moment().isSameOrBefore(visibleTo, "day");

            return isFromInRange && isToInRange;
        }

        return false;
    }, [notificationBannerDetails?.visibleFrom, notificationBannerDetails?.visibleTo]);

    const isVisibleForUserRole = useMemo(() => {
        const visibleForUserRoles = notificationBannerDetails?.visibleForUserRoles;

        if(!visibleForUserRoles) {
            return true;
        } else {
            if (authenticated) {
                const userRoles = visibleForUserRoles.map((role) => ROLES_MAP[role]);
                return userRoles.some((role) => hasOneOfRoles(role));
            } else {
                return false;
            }
        }
    }, [notificationBannerDetails?.visibleForUserRoles, authenticated]);

    const isVisibleForUserCountry = useMemo(() => {
        const userCountryCollection = notificationBannerDetails?.userCountryCollection?.items;

        if (userCountryCollection?.length === 0) {
            return true;
        } else {
            if (userCountry && userCountryCollection?.length) {
                return !userCountryCollection.some(country => country.countryCode === userCountry.toUpperCase());
            } else {
                return false;
            }
        }
    }, [notificationBannerDetails?.userCountryCollection?.items?.length, userCountry]);

    const isExcludedFromCurrentRoute = useMemo(() => {
        const routeCollection = notificationBannerDetails?.excludeFromRoutes;

        if (!routeCollection || routeCollection.length === 0) {
            return false;
        } else {
            let res = false;
            for (let i = 0; i < routeCollection.length; i++) {
                let route = routeCollection[i];

                if(route.endsWith("*")) {
                    // remove trailing asterisks
                    route = route.replace(/\*+$/, "");

                    if(isRouteInLocation(route)) {
                        res = true;
                        break;
                    }
                } else {
                    // add trailing slash if not present
                    if(!route.endsWith("/")) {
                        route = route + "/";
                    }
                    if(isRouteTheExactLocation(route)) {
                        res = true;
                        break;
                    }
                }
            }
            return res;
        }
    }, [notificationBannerDetails?.excludeFromRoutes?.length, location?.pathname]);

    const showVotingNotificationBanner = useMemo(() => {
        if(userCanVote && notificationBannerBySlugDetails?.visibleFrom && notificationBannerBySlugDetails?.visibleTo && userId) {
            return moment().isSameOrAfter(notificationBannerBySlugDetails?.visibleFrom, "day") && moment().isSameOrBefore(notificationBannerBySlugDetails?.visibleTo, "day");
        }
        return false;
    }, [userCanVote, notificationBannerBySlugDetails?.visibleFrom, notificationBannerBySlugDetails?.visibleTo, userId]);

    const showTrialNotificationBanner = useCallback(() => {
        if (!isNotificationBannerVisible && trialReachedLimit && notificationBannerBySlugDetails && !isNotificationBannerBySlugClosed) {
            if (!firstImpressionGAEvent) {
                sendCustomEvent({
                    event_name: eventNames.TRIALPROMPT_BANNER_IMPRESSION
                });
                setFirstImpressionGAEvent(GA_TRIAL_BANNER_FIRST_IMPRESSION, "seen_by_user");
            }
            return true;
        }
    }, [isNotificationBannerVisible, trialReachedLimit, notificationBannerBySlugDetails, isNotificationBannerBySlugClosed, firstImpressionGAEvent]);

    useEffect(() => {
        getNotificationBanner();
    }, []);

    useEffect(() => {
        const visibleFrom = notificationBannerDetails?.visibleFrom;

        if (visibleFrom && !closeBannerButtonClicked?.visibility) {
            const visibleFromDate = new Date(visibleFrom);
            const closeClickedDate = new Date(closeBannerButtonClicked?.dateFrom);
            if (visibleFromDate.getDate() !== closeClickedDate.getDate()) {
                setCloseBannerButtonClicked(TOP_NOTIFICATION_BANNER_VISIBLE, {visibility: true, dateFrom: notificationBannerDetails?.visibleFrom});
            }
        }

        setNotificationBannerVisible(isVisibleForRange && isVisibleForUserRole && isVisibleForUserCountry && !isExcludedFromCurrentRoute && closeBannerButtonClicked?.visibility);
    }, [notificationBannerDetails?.visibleFrom, isVisibleForRange, isVisibleForUserRole, isVisibleForUserCountry, isExcludedFromCurrentRoute, closeBannerButtonClicked?.visibility]);

    useEffect(() => {
        if(authenticated && isTrialMember()) {
            getReservations([RESERVATION_STATUS.UPCOMING, RESERVATION_STATUS.PAST]);
        }
    }, [authenticated]);

    useEffect(() => {
        if(reservations?.length >= TRIAL_TRIPS_MAX) {
            setTrialReachedLimit(true);
        }
    }, [reservations]);

    useEffect(() => {
        if (userCanVote) {
            getNotificationBannerBySlug({variables: {slug: "nimbus-evoting-login-banner", locale: localeUppercase}});

            // use also FF value
            checkHideVotingBanner(userId).then((res) => {
                setIsVotingBannerHidden(res);
            });
        } else if(trialReachedLimit) {
            getNotificationBannerBySlug({variables: {slug: "trial-bookings-limit", locale: localeUppercase}});
        }
    }, [userCanVote, trialReachedLimit]);

    const handleNotificationBannerActionClick = () => {
        const ctaUrl = notificationBannerDetails?.ctaLinksCollection?.items[0]?.url;
        const openInNewTab = notificationBannerDetails?.openCtaInNewTab;

        if (ctaUrl) {
            const isExternalUrl = ctaUrl.startsWith("http://") || ctaUrl.startsWith("https://");

            if (isExternalUrl) {
                if (openInNewTab) {
                    window.open(ctaUrl, "_blank", "noopener, noreferrer");
                } else {
                    window.open(ctaUrl, "_self");
                }
            } else {
                if (openInNewTab) {
                    window.open(getLocalizedUrl(ctaUrl), "_blank");
                } else {
                    navigate(ctaUrl);
                }
            }
        } else if (notificationBannerDetails?.ctaLinksCollection?.items[0]?.contentPage?.slug) {
            navigate("/" + notificationBannerDetails.ctaLinksCollection.items[0].contentPage.slug);
        } else if(notificationBannerDetails?.dialogText) {
            handleOpenDialog();
        }
    };

    const handleOpenDialog = () => {
        setDetailsOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setDetailsOpenDialog(false);
    };

    const handleOpenBuySharesDialog = () => {
        sendCustomEvent({
            event_name: eventNames.TRIALPROMPT_BANNER_BEMEMBER
        });
        setIsBuySharesDialogOpen(true);
    };

    const handleCloseBuySharesDialog = () => {
        setIsBuySharesDialogOpen(false);
    };

    const handleCloseNotificationBanner = () => {
        setCloseBannerButtonClicked(TOP_NOTIFICATION_BANNER_VISIBLE, {visibility: false, dateFrom: notificationBannerDetails?.visibleFrom});
    };

    const handleCloseTrialNotificationBanner = () => {
        sendCustomEvent({
            event_name: eventNames.TRIALPROMPT_BANNER_CLOSE
        });
        setNotificationBannerBySlugClosed(true);
    };

    const handleSubtitleClick = (event) => {
        if (event?.target?.href) {
            sendCustomEvent({
                event_name: eventNames.TRIALPROMPT_BANNER_CONTACTUS
            });
        }
    };

    const actionButton = (item, handler) => {
        if(item) {
            return (
                <CustomButton
                    color="grey"
                    variant={item.type?.toLowerCase()}
                    size={isExtraSmallScreen() ? "small" : (isSmallScreen() ? "medium" : "large")}
                    onClick={handler}
                    fullWidth={isExtraSmallScreen()}
                >
                    {item.label}
                </CustomButton>
            );
        } else {
            return null;
        }
    };

    return (
        <>
            {isNotificationBannerVisible && (
                <>
                    <NotificationBanner
                        title={notificationBannerDetails?.bannerTitle}
                        subtitle={notificationBannerDetails?.bannerSubtitle}
                        imageUrl={notificationBannerDetails?.bannerIcon?.url}
                        imageTitle={notificationBannerDetails?.bannerIcon?.title}
                        actionButton={actionButton(notificationBannerDetails?.ctaLinksCollection?.items[0], handleNotificationBannerActionClick)}
                        notificationType={notificationBannerDetails?.notificationType ? notificationBannerDetails?.notificationType : "info"}
                        closeButton={notificationBannerDetails?.hasCloseButton}
                        onCloseButtonClick={handleCloseNotificationBanner}
                    />

                    <ResponsiveDialog
                        open={isOpenDetailsDialog}
                        handleClose={handleCloseDialog}
                        hasCloseButton
                        disableBackdropClick
                        disableEscapeKeyDown
                        content={(
                            <Box
                                display={"flex"}
                                alignItems={"center"}
                                flexDirection={"column"}
                                textAlign={"center"}
                            >
                                {notificationBannerDetails?.dialogIcon && (
                                    <img
                                        src={retrieveContentfulImage(notificationBannerDetails?.dialogIcon?.url)}
                                        alt={`${notificationBannerDetails?.dialogIcon?.title}`}
                                        width="100"
                                        height="100"
                                    />
                                )}
                                {notificationBannerDetails?.dialogText && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        flexDirection="column"
                                        textAlign={isExtraSmallScreen() ? "justify" : "center"}
                                    >
                                        <Box
                                            className={classes.bullets}
                                            mt={3}
                                        >
                                            <CustomRichText
                                                document={notificationBannerDetails ?.dialogText?.json}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                        actions={
                            <CustomButton
                                variant={"outlined"}
                                color={"black"}
                                size={"medium"}
                                onClick={handleCloseDialog}
                            >
                                {i18n("common.label.close")}
                            </CustomButton>
                        }
                    />
                </>
            )}

            {showTrialNotificationBanner() && (
                <>
                    <NotificationBanner
                        title={notificationBannerBySlugDetails?.bannerTitle}
                        subtitle={notificationBannerBySlugDetails?.bannerSubtitle}
                        imageUrl={notificationBannerBySlugDetails?.bannerIcon?.url}
                        imageTitle={notificationBannerBySlugDetails?.bannerIcon?.title}
                        actionButton={actionButton(notificationBannerBySlugDetails?.ctaLinksCollection?.items[0], handleOpenBuySharesDialog)}
                        notificationType={notificationBannerBySlugDetails?.notificationType ? notificationBannerBySlugDetails?.notificationType : "info"}
                        closeButton={notificationBannerBySlugDetails?.hasCloseButton}
                        onCloseButtonClick={handleCloseTrialNotificationBanner}
                        onSubtitleClick={handleSubtitleClick}
                    />

                    <BuyAShareDialogWrapper
                        isOpen={isBuySharesDialogOpen}
                        onClose={handleCloseBuySharesDialog}
                    />
                </>
            )}

            {showVotingNotificationBanner && !isVotingBannerHidden && (
                <NotificationBanner
                    title={notificationBannerBySlugDetails?.bannerTitle}
                    subtitle={notificationBannerBySlugDetails?.bannerSubtitle}
                    imageUrl={notificationBannerBySlugDetails?.bannerIcon?.url}
                    imageTitle={notificationBannerBySlugDetails?.bannerIcon?.title}
                    actionButton={
                        <NimbusConnectButton
                            variant={notificationBannerBySlugDetails?.ctaLinksCollection?.items[0]?.type?.toLowerCase() || "outlined"}
                            color="black"
                            openInNewTab={notificationBannerBySlugDetails?.openCtaInNewTab}
                            size={isExtraSmallScreen() ? "small" : (isSmallScreen() ? "medium" : "large")}
                            fullWidth={isExtraSmallScreen()}
                            snackbarVertical="bottom"
                            snackbarDirection="up"
                        >
                            {notificationBannerBySlugDetails?.ctaLinksCollection?.items[0]?.label}
                        </NimbusConnectButton>
                    }
                    notificationType={notificationBannerBySlugDetails?.notificationType ? notificationBannerBySlugDetails?.notificationType : "info"}
                />
            )}
        </>
    );
};

TopNotificationBanner.propTypes = {

};

export default TopNotificationBanner;
